import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, Loader2,
  ThumbsUp, ThumbsDown, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ClickToEditPost } from "@/components/click-to-edit-post";
import { PostCoach } from "@/components/post-coach";
import type { Preset } from "@/lib/studio-presets";
import { useToast } from "@/hooks/use-toast";
import { StudioDraftBattle } from "@/components/studio-draft-battle";
import { StudioRefineControls } from "@/components/studio-refine-controls";
import { StudioCopyShareBar } from "@/components/studio-copy-share-bar";

type StudioDraft = {
  content: string;
  style: string;
  description: string;
};

export interface StudioOutputPanelProps {
  studioDrafts: StudioDraft[] | null;
  isGenerating: boolean;
  studioActiveDraftIdx: number;
  setStudioActiveDraftIdx: (i: number) => void;
  setStudioDrafts: React.Dispatch<React.SetStateAction<StudioDraft[] | null>>;
  studioUndoStack: string[];
  setStudioUndoStack: React.Dispatch<React.SetStateAction<string[]>>;
  fetchStudioCoachTip: (content: string) => void;
  output: string;
  setOutput: React.Dispatch<React.SetStateAction<string>>;
  refining: string | null;
  copied: boolean;
  setCopied: (v: boolean) => void;
  copiedPlatform: string | null;
  platformCopyOpen: boolean;
  setPlatformCopyOpen: (v: boolean) => void;
  smartCopyLabel: string | null;
  shared: boolean;
  setShared: (v: boolean) => void;
  saved: boolean;
  setSaved: (v: boolean) => void;
  isSaving: boolean;
  feedbackGiven: "up" | "down" | null;
  feedbackLoading: boolean;
  selectedPreset: Preset | null;
  sessionId: string;
  outputRef: React.RefObject<HTMLDivElement>;
  studioCoachTip: string | null;
  studioCoachLoading: boolean;
  studioCoachDismissed: boolean;
  setStudioCoachDismissed: (v: boolean) => void;
  voiceProfileDetected: boolean | null;
  generationId: number;
  setFeedbackGiven: (v: "up" | "down" | null) => void;
  setRemixOpen: (v: boolean) => void;
  setRemixSourceFormat: (v: string) => void;
  setRemixSourceText: (v: string | null) => void;
  handleCopy: () => Promise<void>;
  handleCopyForPlatform: (id: string, fmt: (s: string) => string) => Promise<void>;
  handleShare: () => void;
  handleRefine: (key: string) => void;
  handleRegenerate: () => void;
  handleSaveToVault: () => Promise<void>;
  handleStudioUndo: () => void;
  handleFeedback: (signal: "up" | "down", draftStyle?: string) => Promise<void>;
  handleFeedbackSilent: (content: string, signal: "up" | "down", draftStyle?: string) => Promise<void>;
  onUndoPush: (prev: string) => void;
}

export function StudioOutputPanel({
  studioDrafts,
  isGenerating,
  studioActiveDraftIdx,
  setStudioActiveDraftIdx,
  setStudioDrafts,
  studioUndoStack,
  setStudioUndoStack,
  fetchStudioCoachTip,
  output,
  setOutput,
  refining,
  copied,
  setCopied,
  copiedPlatform,
  platformCopyOpen,
  setPlatformCopyOpen,
  smartCopyLabel,
  shared,
  setShared,
  saved,
  setSaved,
  isSaving,
  feedbackGiven,
  feedbackLoading,
  selectedPreset,
  sessionId,
  outputRef,
  studioCoachTip,
  studioCoachLoading,
  studioCoachDismissed,
  setStudioCoachDismissed,
  voiceProfileDetected,
  generationId,
  setFeedbackGiven,
  setRemixOpen,
  setRemixSourceFormat,
  setRemixSourceText,
  handleCopy,
  handleCopyForPlatform,
  handleShare,
  handleRefine,
  handleRegenerate,
  handleSaveToVault,
  handleStudioUndo,
  handleFeedback,
  handleFeedbackSilent,
  onUndoPush,
}: StudioOutputPanelProps) {
  const { toast } = useToast();
  return (
    <>
      <StudioDraftBattle
        studioDrafts={studioDrafts}
        isGenerating={isGenerating}
        studioActiveDraftIdx={studioActiveDraftIdx}
        setStudioActiveDraftIdx={setStudioActiveDraftIdx}
        setStudioDrafts={setStudioDrafts}
        setOutput={(v) => setOutput(v as string)}
        setStudioUndoStack={setStudioUndoStack}
        setSaved={setSaved}
        setFeedbackGiven={setFeedbackGiven}
        fetchStudioCoachTip={fetchStudioCoachTip}
        handleFeedbackSilent={handleFeedbackSilent}
        outputRef={outputRef}
      />

      {/* Output */}
      <AnimatePresence>
        {output && (
          <motion.div
            ref={outputRef}
            key="output"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className={`rounded-xl border border-border/50 bg-card overflow-hidden shadow-sm transition-opacity duration-200 ${refining ? "opacity-60 pointer-events-none" : ""}`}
            data-output-card
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "c" && !e.shiftKey) {
                const active = document.activeElement;
                if (e.currentTarget.contains(active as Node)) {
                  e.preventDefault();
                  handleCopy().then(() => {
                    toast({ title: "Copied to clipboard" });
                  });
                }
              }
            }}
          >
            {/* Output card header */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/30">
              <div className="flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded flex items-center justify-center"
                  style={{ background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(205 88% 52%))" }}
                >
                  {refining
                    ? <Loader2 className="w-2.5 h-2.5 text-white animate-spin" />
                    : <Sparkles className="w-2.5 h-2.5 text-white" />
                  }
                </div>
                <p className="text-xs font-semibold text-foreground">
                  {selectedPreset?.label ?? "Your content"}
                </p>
                <span className="text-[10px] text-muted-foreground/40">
                  {output.trim().split(/\s+/).length} words · {output.length} chars
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRegenerate}
                  disabled={isGenerating || !!refining}
                  className="h-7 px-2.5 text-[11px] text-muted-foreground hover:text-foreground"
                >
                  Rewrite
                </Button>
              </div>
            </div>

            <div className="px-4 py-3.5">
              <ClickToEditPost
                key={generationId}
                value={output ?? ""}
                onChange={(v) => {
                  setStudioUndoStack(prev => [output ?? "", ...prev].slice(0, 10));
                  setOutput(v);
                }}
                sessionId={sessionId}
                onUndoPush={(prev) => setStudioUndoStack(p => [prev, ...p].slice(0, 10))}
                placeholder=""
                readOnly={!!refining || isGenerating}
              />
            </div>

            <StudioCopyShareBar
              copied={copied}
              refining={refining}
              handleCopy={handleCopy}
              shared={shared}
              handleShare={handleShare}
              platformCopyOpen={platformCopyOpen}
              setPlatformCopyOpen={setPlatformCopyOpen}
              smartCopyLabel={smartCopyLabel}
              copiedPlatform={copiedPlatform}
              handleCopyForPlatform={handleCopyForPlatform}
            />

            {/* Thumbs feedback */}
            <div className="px-4 pb-3 flex items-center gap-2">
              <span className="text-[10px] text-muted-foreground/50 mr-1">Does this sound like you?</span>
              <button
                type="button"
                data-testid="feedback-thumb-up"
                onClick={() => handleFeedback("up")}
                disabled={!!feedbackGiven || feedbackLoading}
                className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg border transition-all duration-150 ${
                  feedbackGiven === "up"
                    ? "border-green-500/50 bg-green-500/10 text-green-500"
                    : "border-border/30 text-muted-foreground/60 hover:border-green-500/40 hover:text-green-500 disabled:opacity-40"
                }`}
              >
                <ThumbsUp className="w-3 h-3" />
                Sounds like me
              </button>
              <button
                type="button"
                data-testid="feedback-thumb-down"
                onClick={() => handleFeedback("down")}
                disabled={!!feedbackGiven || feedbackLoading}
                className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg border transition-all duration-150 ${
                  feedbackGiven === "down"
                    ? "border-red-500/50 bg-red-500/10 text-red-500"
                    : "border-border/30 text-muted-foreground/60 hover:border-red-500/40 hover:text-red-400 disabled:opacity-40"
                }`}
              >
                <ThumbsDown className="w-3 h-3" />
                Doesn't sound like me
              </button>
            </div>

            <StudioRefineControls
              refining={refining}
              isGenerating={isGenerating}
              handleRefine={handleRefine}
              output={output}
              selectedPreset={selectedPreset}
              setRemixSourceText={setRemixSourceText}
              setRemixSourceFormat={setRemixSourceFormat}
              setRemixOpen={setRemixOpen}
              studioUndoStack={studioUndoStack}
              handleStudioUndo={handleStudioUndo}
              saved={saved}
              isSaving={isSaving}
              handleSaveToVault={handleSaveToVault}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* AI Coach */}
      <AnimatePresence>
        {output && !studioCoachDismissed && (studioCoachLoading || studioCoachTip) && (
          <PostCoach
            tip={studioCoachTip}
            isLoading={studioCoachLoading}
            className="mt-3"
            onDismiss={() => setStudioCoachDismissed(true)}
          />
        )}
      </AnimatePresence>

      {/* Voice training nudge */}
      <AnimatePresence>
        {output && voiceProfileDetected === false && (
          <motion.div
            key="voice-nudge"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, delay: 0.15 }}
            className="mt-3 px-3 py-2.5 rounded-xl border border-border/30 bg-secondary/20 flex items-center gap-2.5"
          >
            <Sparkles className="w-3.5 h-3.5 shrink-0 text-muted-foreground/50" />
            <p className="text-[11px] text-muted-foreground/60 flex-1">
              Add writing samples to match your voice
            </p>
            <a
              href={`${import.meta.env.BASE_URL.replace(/\/$/, "")}/training`}
              className="text-[11px] font-medium text-primary/70 hover:text-primary transition-colors whitespace-nowrap"
            >
              Add samples →
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
